import { priorityIndex } from './stage'
import type { WorkIssue } from '@/types/github'

// User-selectable sort for issue lists/boards. "Order" is each view's natural
// default (priority then recency for flat lists; the per-context manual order
// for boards — the caller supplies that comparator). The rest are uniform field
// sorts.
export type SortKey = 'order' | 'priority' | 'updated' | 'created' | 'assignee' | 'client' | 'title'

export const SORT_OPTIONS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'order', label: 'Order' },
  { key: 'priority', label: 'Priority' },
  { key: 'updated', label: 'Last updated' },
  { key: 'created', label: 'Newest' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'client', label: 'Client' },
  { key: 'title', label: 'Title' },
]

export function isSortKey(value: string | null | undefined): value is SortKey {
  return !!value && SORT_OPTIONS.some((o) => o.key === value)
}

type Compare = (a: WorkIssue, b: WorkIssue) => number

// Final tiebreak so every sort is a total, deterministic order (issue numbers
// are unique). Without it, equal keys (e.g. same updatedAt) leave arbitrary order.
const byNumber: Compare = (a, b) => a.number - b.number

const byUpdated: Compare = (a, b) => b.updatedAt.localeCompare(a.updatedAt) || byNumber(a, b)
const byPriority: Compare = (a, b) => priorityIndex(a.priority) - priorityIndex(b.priority) || byUpdated(a, b)

// Unassigned / no-client sort last, then fall back to the priority order so
// ties stay stable and meaningful.
const firstAssignee = (i: WorkIssue) => i.assignees[0]?.login ?? '￿'

const FIELD_COMPARES: Record<Exclude<SortKey, 'order'>, Compare> = {
  priority: byPriority,
  updated: byUpdated,
  created: (a, b) => b.createdAt.localeCompare(a.createdAt) || byNumber(a, b),
  assignee: (a, b) => firstAssignee(a).localeCompare(firstAssignee(b)) || byPriority(a, b),
  client: (a, b) => (a.client ?? '￿').localeCompare(b.client ?? '￿') || byPriority(a, b),
  title: (a, b) => a.title.localeCompare(b.title) || byNumber(a, b),
}

/** Comparator for a sort key. `order` defers to the caller's natural order. */
export function comparatorFor(key: SortKey, order: Compare): Compare {
  return key === 'order' ? order : FIELD_COMPARES[key]
}

/** The flat-list default order: priority band, then most-recently updated. */
export const byDefaultOrder: Compare = byPriority

/** Sort a flat list by key (non-mutating). `order` = priority then recency. */
export function sortIssues(issues: WorkIssue[], key: SortKey): WorkIssue[] {
  return [...issues].sort(comparatorFor(key, byDefaultOrder))
}
